import * as React from 'react';

import { useLinks, useTheme, useStrings } from 'src/providers/';

import { StyledFAQ } from './StyledFAQ';
import { BackButton } from './BackButton';
import Query from './Query';
import { LinkButton } from 'src/components/Button/LinkButton';

interface IFAQProps {
  onBackRequested: React.MouseEventHandler<HTMLButtonElement>;
  onFaqExpanded: (question: string, index: string) => void;
}

const FAQ = (props: IFAQProps) => {
  const { onBackRequested, onFaqExpanded } = props;
  const { merchantColor } = useTheme();
  const { learnMore } = useLinks();
  const {
    faqHeadline,
    learnMoreLink,
    faq1_answer,
    faq1_question,
    faq2_answer,
    faq2_question,
    faq3_answer,
    faq3_question,
    faq4_answer,
    faq4_question,
    faq5_answer,
    faq5_question,
    faq6_answer,
    faq6_question,
  } = useStrings();

  const backButtonRef: React.RefObject<HTMLButtonElement> = React.useRef(null);
  const [openQuery, setOpenQuery] = React.useState('');

  React.useEffect(() => {
    backButtonRef?.current?.focus();
  }, [backButtonRef]);

  const handleQueryClick = (query: string, question: string, index: string) => {
    const nextOpenQuery = query === openQuery ? '' : query; // if same query is clicked, close it

    if (nextOpenQuery !== '') {
      onFaqExpanded(question, index);
    }
    setOpenQuery(nextOpenQuery);
  };

  const maybeGiftFaq = () => {
    return faq6_answer &&
      faq6_question && (
      <>
        <div className="break" />

        <Query
          answer={faq6_answer}
          ariaId='faq6_desc'
          isOpen={openQuery === 'faq6'}
          question={faq6_question}
          toggle={() => { handleQueryClick('faq6', faq6_question, '6'); }}
        />
      </>
    );
  };

  return (
    <StyledFAQ className="faq-tab" id="faq_tab" merchantColor={merchantColor}>
      <div className="faq-tab__header">
        <BackButton onClick={onBackRequested} ref={backButtonRef} />
        <h2 className="faq-tab__header__title" data-test="faq-header">
          {faqHeadline}
        </h2>
      </div>

      <dl className="faq-tab__content" role="presentation">
        <Query
          answer={faq1_answer}
          ariaId="faq1_desc"
          isOpen={openQuery === 'faq1'}
          question={faq1_question}
          toggle={() => { handleQueryClick('faq1', faq1_question, '1'); }} />

        <div className="break" />

        <Query
          answer={faq2_answer}
          ariaId="faq2_desc"
          isOpen={openQuery === 'faq2'}
          question={faq2_question}
          toggle={() => { handleQueryClick('faq2', faq2_question, '2'); }} />

        <div className="break" />

        <Query
          answer={faq3_answer}
          ariaId="faq3_desc"
          isOpen={openQuery === 'faq3'}
          question={faq3_question}
          toggle={() => { handleQueryClick('faq3', faq3_question, '3'); }} />

        <div className="break" />

        <Query
          answer={faq4_answer}
          ariaId="faq4_desc"
          isOpen={openQuery === 'faq4'}
          question={faq4_question}
          toggle={() => { handleQueryClick('faq4', faq4_question, '4'); }} />

        <div className="break" />

        <Query
          answer={faq5_answer}
          ariaId="faq5_desc"
          isOpen={openQuery === 'faq5'}
          question={faq5_question}
          toggle={() => { handleQueryClick('faq5', faq5_question, '5'); }} />

        {maybeGiftFaq()}
      </dl>

      <div className="faq-tab__footer" id="learnmoretext">
        <div className="faq-tab__footer__text">
          <LinkButton
            data-test="faq-learn-more"
            href={learnMore}
            id="learn-more-link"
            target="_blank"
            rel="noreferrer"
          >
            {learnMoreLink}
          </LinkButton>
        </div>
      </div>
    </StyledFAQ>
  );
};

export default FAQ;
